import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { api, apiSubmit, ApiError, PackageSummary, STAGE_LABEL, rmb } from '../lib/api';
import { DemoDataPanel } from '../components/DemoDataPanel';
import { ProjectPicker, type PickedProject } from '../components/ProjectPicker';
import { Skeleton } from '../components/ui/Skeleton';
import { EmptyState } from '../components/ui/EmptyState';

function NewPackageForm() {
  const qc = useQueryClient();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [project, setProject] = useState<PickedProject | null>(null);
  const [envelope, setEnvelope] = useState('');
  // 改改 6/29: when a same-named package already exists, don't silently make a duplicate — surface it.
  const [dup, setDup] = useState<{ id: string; name: string } | null>(null);
  const create = useMutation({
    mutationFn: (force?: boolean) => apiSubmit<{ id: string }>('/packages', {
      name,
      project_name: project?.name || null,
      wtl_project_uuid: project?.uuid || null,
      budget_envelope_rmb: envelope ? Number(envelope) : null,
      ...(force ? { force: true } : {}),
    }),
    onSuccess: (p) => { qc.invalidateQueries({ queryKey: ['packages'] }); nav(`/packages/${p.id}`); },
    onError: (e) => { const b = (e as ApiError)?.body; if (b?.error === 'duplicate_name' && b?.existing) setDup(b.existing); },
  });
  if (!open) return <button className="btn-primary" onClick={() => setOpen(true)}>+ 新建采购包 / New Package</button>;
  return (
    <div className="card enter-pop" style={{ background: '#fff' }}>
      <strong>新建采购包 / New Package</strong>
      <div className="row-wrap" style={{ gap: 10, marginTop: 8 }}>
        <div className="field" style={{ flex: '2 1 240px', marginBottom: 0 }}><label>名称 / Name *</label><input value={name} onChange={(e) => setName(e.target.value)} placeholder="如：歌尔小酒店精装修" /></div>
        <div className="field" style={{ flex: '1 1 240px', marginBottom: 0 }}><label>项目 / Project（关联 WTL 项目）</label><ProjectPicker selected={project} onPick={setProject} allowFreeText placeholder="搜索 WTL 项目… / search WTL project" /></div>
        <div className="field" style={{ flex: '0 0 150px', marginBottom: 0 }}><label>测算价 / Envelope</label><input type="number" value={envelope} onChange={(e) => setEnvelope(e.target.value)} placeholder="可留空" /></div>
      </div>
      <div className="row" style={{ marginTop: 10 }}>
        <button className="btn-primary" disabled={!name || create.isPending} onClick={() => { setDup(null); create.mutate(false); }}>{create.isPending ? '创建中…' : '创建 / Create'}</button>
        <button onClick={() => setOpen(false)}>取消</button>
        <span className="small muted">创建后即可在详情页"从 xls 导入"。/ Then import the xls on the detail page.</span>
      </div>
      {/* 改改 6/29: a same-named package already exists — offer to open it (avoids the duplicate-package split). */}
      {dup && (
        <div className="card enter-pop" style={{ marginTop: 8, borderColor: 'var(--warn)', background: 'var(--warn-soft)', fontSize: 13 }}>
          ⚠ 已存在同名采购包「{dup.name}」。/ A package with this name already exists.
          <div className="row" style={{ gap: 8, marginTop: 6 }}>
            <button className="btn-primary" onClick={() => nav(`/packages/${dup.id}`)}>打开已有 / Open existing</button>
            <button onClick={() => { setDup(null); create.mutate(true); }}>仍要新建 / Create anyway</button>
          </div>
        </div>
      )}
      {create.isError && !dup && (
        <div className="small" style={{ color: 'var(--bad)', marginTop: 6 }}>
          {(create.error as ApiError)?.status === 401
            ? <>未登录或登录已过期 — 请先<Link to="/login">登录</Link>再创建。 / Please sign in first.</>
            : String((create.error as ApiError)?.body?.error ?? (create.error as any)?.message)}
        </div>
      )}
    </div>
  );
}

export default function Dashboard() {
  const { data: packages, isLoading } = useQuery({ queryKey: ['packages'], queryFn: () => api<PackageSummary[]>('/packages') });

  return (
    <div>
      <h2 style={{ marginTop: 0 }}>采购包 / Procurement Packages</h2>
      <p className="lede">Slice 0 的确定性主轴：建包 → 录报价 → 清标 → 对比 → 群评审 → 定标，全程零大模型调用。 / The deterministic spine: intake → bids → cleaning → compare → group review → award, with zero LLM calls.</p>

      <div style={{ marginBottom: 14 }}><NewPackageForm /></div>

      <DemoDataPanel />

      {isLoading ? (
        <div className="card"><Skeleton lines={4} /></div>
      ) : !packages?.length ? (
        <EmptyState title="暂无采购包 / No packages yet" hint="先加载上方的演示数据，或新建一个采购包。 / Load the demo data above, or create a package." />
      ) : (
        <div className="card" style={{ padding: 0 }}>
          <table className="tbl-striped tbl-hover">
            <thead>
              <tr><th>采购包 / Package</th><th>项目 / Project</th><th>模式 / Mode</th><th>测算价 / Envelope</th><th>阶段 / Stage</th><th>劳务方 / Bidders</th><th></th></tr>
            </thead>
            <tbody>
              {packages.map((p) => (
                <tr key={p.id}>
                  <td><strong>{p.name}</strong></td>
                  <td className="muted">{p.project_name ?? '—'}</td>
                  <td><span className="pill pill-muted">{p.procurement_mode}</span></td>
                  <td>{rmb(p.budget_envelope_rmb)}</td>
                  <td>{p.awarded_engagement_id ? <span className="pill pill-good">已定标 / Awarded</span> : <span className="pill pill-stage">{STAGE_LABEL[p.current_stage] ?? `stage ${p.current_stage}`}</span>}</td>
                  <td>{p.engagement_count}</td>
                  <td><Link className="btn" to={`/packages/${p.id}`} style={{ whiteSpace: 'nowrap' }}>打开 / Open →</Link></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
